import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/core/data-fetching/queryKeys";
import { resolveError } from "@/core/errors/resolveError";
import { useAssignablePlansQuery } from "./useAssignablePlansQuery";
import { allMigrationCompanies, reviewCompanyMigration, executeCompanyMigration, migrationError, type MigrationPreview } from "../api/company-migration.service";
import type { Company } from "../api/companies.service";
export interface Draft { planId: string; seats?: number; channels?: number; preview?: MigrationPreview; requestKey?: string; status?: 'ready' | 'running' | 'done' | 'error'; error?: string; invoiceId?: string | null }
export interface Row { company: Company; draft: Draft }

export function useCompanyBulkMigration(selected: Company[] | undefined, onSaved: () => Promise<void>) {
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const candidates = useQuery({ queryKey: queryKeys.companies.migrationCandidates, queryFn: allMigrationCompanies, enabled: !selected });
  const plans = useAssignablePlansQuery(true);
  const companies = selected ?? candidates.data ?? [];
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [defaultPlan, setDefaultPlan] = useState('');
  const [noExternalCharges, setNoExternalCharges] = useState(false);
  const [started, setStarted] = useState(false);
  const [search, setSearch] = useState('');
  const [operationError, setOperationError] = useState('');
  const rows: Row[] = companies.map((company) => ({ company, draft: drafts[company.id] ?? { planId: defaultPlan } }));
  const update = (id: string, patch: Partial<Draft>) => setDrafts((previous) => ({ ...previous, [id]: { ...(previous[id] ?? { planId: defaultPlan }), ...patch } }));
  const review = useMutation({ mutationFn: async () => {
    setOperationError('');
    for (const { company, draft } of rows.filter((row) => row.draft.planId && row.draft.status !== 'done')) {
      if (!mounted.current) break;
      update(company.id, { status: 'running', error: undefined, preview: undefined });
      try {
        const preview = await reviewCompanyMigration(company.id, { planId: draft.planId, seatCount: draft.seats, additionalConnectionCount: draft.channels });
        update(company.id, { preview, seats: preview.seatCount, channels: preview.additionalConnectionCount, requestKey: crypto.randomUUID(), status: 'ready' });
      } catch (error) { update(company.id, { status: 'error', error: migrationError(error) ?? resolveError(error).message }); }
    }
  } });
  const execute = useMutation({ mutationFn: async () => {
    setStarted(true); setOperationError('');
    for (const { company, draft } of rows.filter((row) => row.draft.preview && row.draft.status !== 'done')) {
      if (!mounted.current) break;
      update(company.id, { status: 'running', error: undefined });
      try {
        const result = await executeCompanyMigration(company.id, draft.preview!, draft.requestKey!);
        update(company.id, { status: 'done', invoiceId: result.invoiceId });
      } catch (error) { update(company.id, { status: 'error', error: migrationError(error) ?? resolveError(error).message }); }
    }
    try { await onSaved(); } catch { setOperationError('O lote terminou. Atualize a lista de empresas para ver os dados mais recentes.'); }
  } });
  const busy = review.isPending || execute.isPending;
  useEffect(() => {
    if (!busy) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);
  const ready = rows.filter((row) => row.draft.preview && row.draft.status !== 'done');
  const done = rows.filter((row) => row.draft.status === 'done').length;
  const options = [{ value: '', label: 'Não migrar' }, ...(plans.data?.data ?? []).map((plan) => ({ value: plan.id, label: plan.name }))];
  return { candidates, plans, companies, rows, update, defaultPlan, setDefaultPlan, setDrafts, noExternalCharges, setNoExternalCharges, started, search, setSearch, operationError, review, execute, busy, ready, done, options };
}
